import { useEffect, useState } from "react";
import { css } from "@emotion/react";

import useForm from "../../../../hooks/useForm";
import { AUTH_URL, getUrl, zitadelRoutes } from "../../../../includes/routes";
import Button from "../../../Button";
import Form from "../../../form/Form";
import LabelledField from "../../../form/LabelledField";
import Input from "../../../form/Input";
import FormActions from "../../../form/FormActions";
import { useZitadel, ZitadelSession } from "../../../../contexts/ZitadelContext";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FAILED_AUTHENTICATION_PARAM, zitadelGraphqlErrors } from "../../../../includes/constants";
import { API_BASE_URL } from "../../../../../config/config";
import Loading from "../../../utils/Loading";

type ZitadelSendOtpEmailCodeMutationResponse = {
    zitadelSendOtpEmailCode: ZitadelSession;
}

type ZitadelSendOtpEmailCodeMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelCancelTotpRegistrationMutationResponse = {
    zitadelCancelOtpEmailRegistration: ZitadelSession;
}

type ZitadelCancelTotpRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelCompleteOtpEmailChallengeMutationResponse = {
    zitadelCompleteOtpEmailChallenge: {
        id: string
    };
}

type ZitadelCompleteOtpEmailChallengeMutationVariables = {
    sessionId: string;
    sessionToken: string;
    code: string;
}

const OtpEmailChallenge = () => {
    const navigate = useNavigate();

    const [isOtpEmailLoading, setIsOtpEmailLoading] = useState(false);
    const [isSendOtpEmailLoading, setIsSendOtpEmailLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [isFallbackRedirectLoading, setIsFallbackRedirectLoading] = useState(false);
    const [isCompletionNoticeVisible, setIsCompletionNoticeVisible] = useState(false);

    const {
        loginInfo,
        clearLoginInfo,
        session,
        setSession,
        clearSession,
    } = useZitadel();

    const { formState, onFieldChange, onFieldInvalid, formErrors, setFormErrors } = useForm({
        fields: { code: "" },
    });

    const [zitadelSendOtpEmailCode] =
        useMutation<ZitadelSendOtpEmailCodeMutationResponse, ZitadelSendOtpEmailCodeMutationVariables>(
            ZITADEL_SEND_OTP_EMAIL_CODE,
        );

    const [zitadelCancelOtpEmailRegistration] =
        useMutation<ZitadelCancelTotpRegistrationMutationResponse, ZitadelCancelTotpRegistrationMutationVariables>(
            ZITADEL_CANCEL_OTP_EMAIL_REGISTRATION,
        );

    const [zitadelCompleteOtpEmailChallenge] =
        useMutation<ZitadelCompleteOtpEmailChallengeMutationResponse, ZitadelCompleteOtpEmailChallengeMutationVariables>(
            ZITADEL_COMPLETE_OTP_EMAIL_CHALLENGE,
        );

    const onSubmit = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register OTP email.");

        setIsOtpEmailLoading(true);

        zitadelCompleteOtpEmailChallenge({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
                code: formState.code,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            if (!loginInfo?.isRedirectDisabled) {
                let url = API_BASE_URL + "/auth/login-redirect";
                if (loginInfo?.backUrl) url += "?BackURL=" + loginInfo.backUrl;
                window.location.href = url;
            } else {
                setIsCompletionNoticeVisible(true);
            }

            clearLoginInfo();
            clearSession();
        }).catch((e) => {
            const graphQlErrors = e.graphQLErrors;
            if (graphQlErrors) {
                if (
                    graphQlErrors.find(
                        (graphQlError: any) => graphQlError.extensions?.details?.includes(
                            zitadelGraphqlErrors.INVALID_MFA_CODE
                        )
                    )
                ) {
                    setFormErrors({ code: "Invalid code" });
                    // Screen reader announces a field error message.
                    const firstTargetKey = Object.keys(formErrors)?.[0] ?? null;
                    if (firstTargetKey) {
                        const firstElement = document.getElementsByName(Object.keys(formErrors)[0]);
                        if (firstElement.length > 0) {
                            firstElement[0].focus();
                        }
                    }
                    return;
                }
            }
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    };

    const onCancel = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to cancel OTP email registration.");

        setIsCancelLoading(true);

        zitadelCancelOtpEmailRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("OTP email registration cancellation did not return a valid response.");

            const session = data.zitadelCancelOtpEmailRegistration;

            setSession(session);

            if (session.metadata.isMfaRegistrationRequired) {
                /** Redirect to MFA initialisation if its still necessary */
                navigate(getUrl(zitadelRoutes.MFA_INITIALISATION.route));
            } else {
                /** Redirect to start of login flow otherwise */
                navigate(getUrl(AUTH_URL));
            }
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }

    const onSendOtpEmail = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register email authentication.");

        setIsSendOtpEmailLoading(true);

        zitadelSendOtpEmailCode({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("OTP email registration did not return a valid response.");

            setIsSendOtpEmailLoading(false);
            setSession(data.zitadelSendOtpEmailCode);
        }).catch(() => {
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    };

    useEffect(function fallbackRedirection() {
        /** Skip fallback redirection if actions are loading since the actions do the redirection themselves */
        if (isOtpEmailLoading || isSendOtpEmailLoading || isCancelLoading) return;

        if (!loginInfo || !session) {
            /** Redirect to start of login flow if missing required variables */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        } else if (!session.metadata.isOtpEmailChallengeRequired) {
            /** Redirect if challenge is not required */
            if (session.metadata.isMfaRegistrationRequired) {
                /** Redirect to MFA initialisation if its still necessary */
                setIsFallbackRedirectLoading(true);
                navigate(getUrl(zitadelRoutes.MFA_INITIALISATION.route));
            } else {
                /** Redirect to start of login flow otherwise */
                setIsFallbackRedirectLoading(true);
                navigate(getUrl(AUTH_URL));
            }
        }
    }, [
        isCancelLoading,
        isOtpEmailLoading,
        isSendOtpEmailLoading,
        loginInfo,
        navigate,
        session,
    ]);

    const isLoading = isFallbackRedirectLoading;
    const isActionsDisabled = isOtpEmailLoading || isSendOtpEmailLoading || isCancelLoading || isLoading;

    /** Completion notice overrides all */
    if (isCompletionNoticeVisible) return <div>Authentication complete, you may now close this window</div>;

    /** Blocking based on loginInfo & session is not actually necessary, but helps with typescript hinting */
    if (isLoading || !loginInfo || !session) return <Loading onTop overlay overlayColour={"white"} />;

    return (
        <div css={authContentStyle}>
            <div css={headerStyle}>
                <h1 css={leftHeaderStyle}>Multi-Factor Authentication</h1>
            </div>
            {session.metadata.isMfaRegistrationRequired && <p>Each time you log in, you will receive a one-time code.</p>}
            <h2>Step 1: Send one-time code</h2>
            {session.metadata.isMfaRegistrationRequired ? (
                <p>Click on the button below to send a one-time code to <b>{session.metadata.userEmail}</b></p>
            ) : (
                <p>
                    Your one-time code has been sent to <b>{session.metadata.userEmail}</b>.
                    <br/>
                    click the button below to receive another.
                </p>
            )}
            <div css={oneTimeCodeButtonContainerStyle}>
                <Button
                    css={buttonStyle}
                    type="button"
                    onClick={onSendOtpEmail}
                    disabled={isActionsDisabled}
                    forceLoading={isSendOtpEmailLoading}
                    useOutline
                >
                    Send a one-time code
                </Button>
            </div>
            <h2>Step 2: Get verification code</h2>
            <p>Enter the code you see you received in your email.</p>
            <Form onSubmit={onSubmit}>
                <LabelledField
                    name="code"
                    error={formErrors.code}
                    label="Authentication Code"
                    version={'normal'}
                >
                    <Input
                        hasError={!!formErrors.code}
                        label="Enter the code from your authenticator app"
                        name="code"
                        onChange={onFieldChange}
                        onInvalid={onFieldInvalid}
                        value={formState.code}
                        hasBorder
                        required
                    />
                </LabelledField>
                <div css={formActionsStyle}>
                    <FormActions customCss={customFormActionsStyles}>
                        {session.metadata.isMfaRegistrationRequired && (
                            <Button
                                css={cancelButtonStyle}
                                type="button"
                                onClick={onCancel}
                                disabled={isActionsDisabled}
                                forceLoading={isCancelLoading}
                                useOutline
                            >
                                Choose another method
                            </Button>
                        )}
                        <Button
                            css={buttonStyle}
                            type="submit"
                            disabled={isActionsDisabled}
                            forceLoading={isOtpEmailLoading}
                            useOutline
                        >
                            Confirm
                        </Button>
                    </FormActions>
                </div>
            </Form>
        </div>
    );
}

const ZITADEL_SEND_OTP_EMAIL_CODE = gql`
    mutation zitadelSendOtpEmailCode($sessionId: String!, $sessionToken: String!) {
        zitadelSendOtpEmailCode(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
            }
        }
    }
`;

const ZITADEL_CANCEL_OTP_EMAIL_REGISTRATION = gql`
    mutation zitadelCancelOtpEmailRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelCancelOtpEmailRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
            }
        }
    }
`;

const ZITADEL_COMPLETE_OTP_EMAIL_CHALLENGE = gql`
    mutation zitadelCompleteOtpEmailChallenge($sessionId: String!, $sessionToken: String!, $code: String!) {
        zitadelCompleteOtpEmailChallenge(sessionId: $sessionId, sessionToken: $sessionToken, code: $code) {
            id
        }
    }
`;

// Copied from SignIn.tsx
const authContentStyle = (theme: EmotionTheme) => css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

// Copied from SignIn.tsx
const headerStyle = css`
    display: flex;
    justify-content: space-between;
`;

// Copied from SignIn.tsx
const leftHeaderStyle = (theme: EmotionTheme) => css`
    margin: 0;
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

// Copied from SignIn.tsx
const formActionsStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        margin-top: 20px;
        justify-content: center;
    }

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }
`;

// Copied from SignIn.tsx
const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

// Copied from SignIn.tsx
const buttonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;
const cancelButtonStyle = (theme: EmotionTheme) => css`
    ${buttonStyle(theme)};
    background: none;
    color: ${theme.colours.blue['400']};
    border: 1px solid ${theme.colours.blue['400']};
`;

const oneTimeCodeButtonContainerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export default OtpEmailChallenge;
